import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';

// Minimal MUI-less UI to avoid dependency assumptions; CRA already has basic CSS
// If project uses MUI elsewhere, this simple form still works without extra imports.

const Field: React.FC<{ label: string; value: string; onChange: (v: string) => void; placeholder?: string }>
  = ({ label, value, onChange, placeholder }) => (
  <div style={{ marginBottom: 12 }}>
    <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>{label}</label>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc' }}
    />
  </div>
);

type WithChildren = { children?: React.ReactNode };

const HelpText: React.FC<WithChildren> = ({ children }) => (
  <div style={{ fontSize: 12, color: '#666', marginTop: -6, marginBottom: 12 }}>{children}</div>
);

const Container: React.FC<WithChildren> = ({ children }) => (
  <div style={{ maxWidth: 720, margin: '24px auto', padding: 16 }}>
    {children}
  </div>
);

const Card: React.FC<WithChildren> = ({ children }) => (
  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
    {children}
  </div>
);

const Button: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & WithChildren> = ({ children, ...rest }) => (
  <button
    {...rest}
    style={{
      background: '#2563eb',
      color: '#fff',
      border: 'none',
      borderRadius: 6,
      padding: '10px 14px',
      fontWeight: 600,
      cursor: 'pointer',
      opacity: (rest.disabled ? 0.6 : 1),
    }}
  >
    {children}
  </button>
);

const DangerText: React.FC<WithChildren> = ({ children }) => (
  <div style={{ color: '#b91c1c', marginTop: 8 }}>{children}</div>
);

const SuccessText: React.FC<WithChildren> = ({ children }) => (
  <div style={{ color: '#065f46', marginTop: 8 }}>{children}</div>
);

const MemberSocialAdmin: React.FC = () => {
  const navigate = useNavigate();
  const { token, keycloak } = useAuth();

  const [bioguideId, setBioguideId] = useState('');
  const [xHandle, setXHandle] = useState('');
  const [facebookHandle, setFacebookHandle] = useState('');
  const [instagramHandle, setInstagramHandle] = useState('');
  const [tiktokHandle, setTiktokHandle] = useState('');
  const [truthSocialHandle, setTruthSocialHandle] = useState('');

  // Listing/search state
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [loading, setLoading] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const isAdmin = useMemo(() => {
    try {
      const tokenParsed: any = keycloak?.tokenParsed || {};
      const groups: string[] = tokenParsed?.groups || tokenParsed?.realm_access?.groups || [] as any;
      const roles: string[] = tokenParsed?.realm_access?.roles || [];
      const clientRoles: string[] = (() => {
        try {
          const ra = tokenParsed?.resource_access || {};
          const all: string[] = [];
          Object.values(ra).forEach((r: any) => {
            if (r && Array.isArray(r.roles)) all.push(...r.roles);
          });
          return all;
        } catch {
          return [];
        }
      })();
      const hasGroup = Array.isArray(groups) && groups.some(g => String(g).replace(/^\//, '') === 'administrator');
      const hasRealmRole = Array.isArray(roles) && roles.includes('administrator');
      const hasClientRole = Array.isArray(clientRoles) && clientRoles.includes('administrator');
      const hasFn = typeof (keycloak as any)?.hasRealmRole === 'function' && (keycloak as any).hasRealmRole('administrator');
      return hasGroup || hasRealmRole || hasClientRole || !!hasFn;
    } catch {
      return false;
    }
  }, [keycloak]);

  React.useEffect(() => {
    apiService.setToken(token);
  }, [token]);

  async function loadList(pageNum = 1) {
    setLoading(true);
    setError(null);
    try {
      const resp = await apiService.listMemberSocialProfiles({ query: query.trim() || undefined, limit: perPage, offset: (pageNum - 1) * perPage });
      const data = resp.data || {};
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Number(data.total || 0));
      setPage(Number(data.page || pageNum));
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    if (isAdmin) {
      loadList(1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!bioguideId.trim()) {
      setError('Bioguide ID is required');
      return;
    }

    const payload: Record<string, string | null> = {
      x_handle: xHandle.trim() || null,
      facebook_handle: facebookHandle.trim() || null,
      instagram_handle: instagramHandle.trim() || null,
      tiktok_handle: tiktokHandle.trim() || null,
      truth_social_handle: truthSocialHandle.trim() || null,
    };

    setSubmitting(true);
    try {
      const resp = await apiService.upsertMemberSocialHandles(bioguideId.trim(), payload);
      setSuccess('Social handles saved successfully.');
      // Optionally clear empty values only
      if (resp?.data) {
        // Hydrate fields back from API response to reflect normalization
        setXHandle(resp.data.x_handle || '');
        setFacebookHandle(resp.data.facebook_handle || '');
        setInstagramHandle(resp.data.instagram_handle || '');
        setTiktokHandle(resp.data.tiktok_handle || '');
        setTruthSocialHandle(resp.data.truth_social_handle || '');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to save social handles';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (!isAdmin) {
    return (
      <Container>
        <Card>
          <h2 style={{ marginTop: 0 }}>Administrator Access Required</h2>
          <p>You must be an administrator to manage member social media handles.</p>
          <Button onClick={() => navigate('/admin')}>Back to Admin</Button>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <h2 style={{ marginTop: 0 }}>Member Social Handles</h2>
        <p>Search for a member, select them to edit their social media handles.</p>

        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or Bioguide ID…"
            style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc' }}
          />
          <Button onClick={() => loadList(1)} disabled={loading}>{loading ? 'Searching…' : 'Search'}</Button>
        </div>

        <div style={{ overflowX: 'auto', marginBottom: 16 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: 8 }}>Member</th>
                <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: 8 }}>Party</th>
                <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: 8 }}>State</th>
                <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: 8 }}>X</th>
                <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: 8 }}>Facebook</th>
                <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: 8 }}>Instagram</th>
                <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: 8 }}>TikTok</th>
                <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: 8 }}>Truth Social</th>
                <th style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb', padding: 8 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.bioguide_id}>
                  <td style={{ borderBottom: '1px solid #f1f5f9', padding: 8 }}>
                    <div style={{ fontWeight: 600 }}>{it.name || it.bioguide_id}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{it.bioguide_id}</div>
                  </td>
                  <td style={{ borderBottom: '1px solid #f1f5f9', padding: 8 }}>{it.party || ''}</td>
                  <td style={{ borderBottom: '1px solid #f1f5f9', padding: 8 }}>{it.state || ''}</td>
                  <td style={{ borderBottom: '1px solid #f1f5f9', padding: 8 }}>{it.x_handle || ''}</td>
                  <td style={{ borderBottom: '1px solid #f1f5f9', padding: 8 }}>{it.facebook_handle || ''}</td>
                  <td style={{ borderBottom: '1px solid #f1f5f9', padding: 8 }}>{it.instagram_handle || ''}</td>
                  <td style={{ borderBottom: '1px solid #f1f5f9', padding: 8 }}>{it.tiktok_handle || ''}</td>
                  <td style={{ borderBottom: '1px solid #f1f5f9', padding: 8 }}>{it.truth_social_handle || ''}</td>
                  <td style={{ borderBottom: '1px solid #f1f5f9', padding: 8 }}>
                    <Button
                      onClick={() => {
                        setBioguideId(it.bioguide_id);
                        setXHandle(it.x_handle || '');
                        setFacebookHandle(it.facebook_handle || '');
                        setInstagramHandle(it.instagram_handle || '');
                        setTiktokHandle(it.tiktok_handle || '');
                        setTruthSocialHandle(it.truth_social_handle || '');
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >Edit</Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ padding: 12, color: '#64748b' }}>{loading ? 'Loading…' : 'No members found.'}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {total > perPage && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
            <button
              onClick={() => { if (page > 1) loadList(page - 1); }}
              disabled={page <= 1 || loading}
              style={{ background: 'transparent', border: '1px solid #cbd5e1', borderRadius: 6, padding: '8px 12px', cursor: 'pointer' }}
            >Prev</button>
            <div style={{ color: '#334155' }}>Page {page} of {Math.max(1, Math.ceil(total / perPage))}</div>
            <button
              onClick={() => { const maxPage = Math.max(1, Math.ceil(total / perPage)); if (page < maxPage) loadList(page + 1); }}
              disabled={page >= Math.max(1, Math.ceil(total / perPage)) || loading}
              style={{ background: 'transparent', border: '1px solid #cbd5e1', borderRadius: 6, padding: '8px 12px', cursor: 'pointer' }}
            >Next</button>
          </div>
        )}

        <h3>Edit Handles</h3>
        <p>Select a member above to load their existing handles, then update as needed.</p>

        <form onSubmit={onSubmit}>
          <Field label="Bioguide ID" value={bioguideId} onChange={setBioguideId} placeholder="e.g. A000360" />
          <HelpText>Find a member's Bioguide ID on the member detail page URL or via the Members search.</HelpText>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <Field label="X (Twitter) Handle" value={xHandle} onChange={setXHandle} placeholder="e.g. RepJohnDoe" />
              <HelpText>Do not include the @ symbol.</HelpText>
            </div>
            <div>
              <Field label="Facebook Page" value={facebookHandle} onChange={setFacebookHandle} placeholder="e.g. RepJohnDoe" />
              <HelpText>Enter the page handle only, not the full URL.</HelpText>
            </div>
            <div>
              <Field label="Instagram Handle" value={instagramHandle} onChange={setInstagramHandle} placeholder="e.g. rep.johndoe" />
              <HelpText>Do not include the @ symbol.</HelpText>
            </div>
            <div>
              <Field label="TikTok Handle" value={tiktokHandle} onChange={setTiktokHandle} placeholder="e.g. repjohndoe" />
              <HelpText>Do not include the @ symbol.</HelpText>
            </div>
            <div>
              <Field label="Truth Social Handle" value={truthSocialHandle} onChange={setTruthSocialHandle} placeholder="e.g. RepJohnDoe" />
              <HelpText>Do not include the @ symbol.</HelpText>
            </div>
          </div>

          <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
            <Button type="submit" disabled={submitting || !bioguideId}>{submitting ? 'Saving…' : 'Save Handles'}</Button>
            <button
              type="button"
              onClick={() => navigate('/admin') }
              style={{ background: 'transparent', border: '1px solid #cbd5e1', borderRadius: 6, padding: '10px 14px', cursor: 'pointer' }}
            >
              Done
            </button>
          </div>

          {error && <DangerText>{error}</DangerText>}
          {success && <SuccessText>{success}</SuccessText>}
        </form>
      </Card>
    </Container>
  );
};

export default MemberSocialAdmin;
